/*
  Comprobantes de las membresías de Conecta Agenda.

  Agenda cobra con MercadoPago y guarda los datos de boleta o factura de cada organizador,
  pero no emite nada: el comprobante lo emite el ERP, a mano. Esta pantalla es la cola de
  ese trabajo, en tres pasos — se encola, se sube el documento emitido, se envía al cliente
  (y recién ahí Agenda se entera).

  El plazo importa: Agenda le promete al cliente 2 días hábiles antes de pagar, así que la
  columna de días hábiles y el KPI "en riesgo" son el centro de la pantalla, no un adorno.
*/

import { useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { Badge, KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { AgendaComprobante, AgendaComprobanteLectura } from '@/lib/types'
import {
  IconAlertTriangle, IconBan, IconCheckCircle, IconExternalLink,
  IconRefresh, IconSearch, IconSend, IconUpload,
} from '@/components/icons/Icons'

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Comprobantes de Agenda' }]

const ESTADO_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  emitido: 'Emitido',
  enviado: 'Enviado',
  descartado: 'Descartado',
}

const ESTADO_BADGE: Record<string, BadgeVariant> = {
  pendiente: 'borrador',
  emitido: 'info',
  enviado: 'completado',
  descartado: 'cancelado',
}

/** El aviso del cron salta pasado 1 día hábil; la pantalla marca lo mismo. */
const LIMITE_DIAS_HABILES = 1

export default function ComprobantesAgenda() {
  const api = useApi()
  const [sincronizando, setSincronizando] = useState(false)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('activos')

  // Modal de emisión
  const [emitir, setEmitir] = useState<AgendaComprobante | null>(null)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [lectura, setLectura] = useState<AgendaComprobanteLectura | null>(null)
  const [serieNumero, setSerieNumero] = useState('')
  const [subiendo, setSubiendo] = useState(false)
  const [guardando, setGuardando] = useState(false)

  // Modal de descarte
  const [descartar, setDescartar] = useState<AgendaComprobante | null>(null)
  const [motivo, setMotivo] = useState('')
  const [refAgenda, setRefAgenda] = useState('')
  const [sacarDeAgenda, setSacarDeAgenda] = useState(true)

  // Un minuto, no cinco: es una cola de tres tiempos que alguien mas puede
  // estar moviendo, y mostrar un comprobante ya emitido como pendiente llevaria
  // a emitirlo dos veces.
  const { data: filas = [], isLoading: loading, refrescar: cargar } = useCachedData(
    ['agenda-comprobantes'],
    () => api.get<AgendaComprobante[]>('/api/agenda-comprobantes').catch((err: any) => {
      showToast(err?.message || 'No se pudo cargar la cola', 'error')
      return [] as AgendaComprobante[]
    }),
    { staleTime: 60 * 1000 },
  )

  async function sincronizar() {
    setSincronizando(true)
    try {
      const r = await api.post<{ nuevos: number; total: number }>('/api/agenda-comprobantes/sync', {})
      showToast(r.nuevos > 0
        ? `${r.nuevos} comprobante${r.nuevos !== 1 ? 's' : ''} nuevo${r.nuevos !== 1 ? 's' : ''} en la cola`
        : 'Sin cobros nuevos por facturar', r.nuevos > 0 ? 'success' : 'info')
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo sincronizar con Agenda', 'error')
    } finally {
      setSincronizando(false)
    }
  }

  function abrirEmision(fila: AgendaComprobante) {
    setEmitir(fila)
    setArchivo(null)
    setLectura(null)
    setSerieNumero(fila.serie_numero || '')
  }

  async function subirPdf(file: File) {
    if (!emitir) return
    setArchivo(file)
    setSubiendo(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await api.upload<AgendaComprobanteLectura>(`/api/agenda-comprobantes/${emitir.id}/comprobante`, fd)
      setLectura(r)
      if (r.sugerencia.serie_numero && !serieNumero) setSerieNumero(r.sugerencia.serie_numero)
    } catch (err: any) {
      showToast(err?.message || 'No se pudo subir el PDF', 'error')
      setArchivo(null)
    } finally {
      setSubiendo(false)
    }
  }

  async function confirmarEmision() {
    if (!emitir) return
    setGuardando(true)
    try {
      await api.post(`/api/agenda-comprobantes/${emitir.id}/emitir`, { serie_numero: serieNumero.trim().toUpperCase() })
      showToast('Comprobante registrado y asentado en caja', 'success')
      setEmitir(null)
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo emitir', 'error')
    } finally {
      setGuardando(false)
    }
  }

  async function enviar(fila: AgendaComprobante) {
    if (!confirm(`¿Enviar ${fila.serie_numero} a ${fila.payer_email}?\n\nSe adjunta el PDF y, al salir el correo, Agenda muestra la serie-número al cliente.`)) return
    try {
      const r = await api.post<AgendaComprobante & { marcado_error: string | null }>(
        `/api/agenda-comprobantes/${fila.id}/enviar`, {})
      showToast(r.marcado_error
        ? `Correo enviado, pero Agenda quedó sin marcar: ${r.marcado_error}`
        : 'Comprobante enviado y marcado en Agenda', r.marcado_error ? 'error' : 'success')
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo enviar', 'error')
    }
  }

  async function reintentarMarcado(fila: AgendaComprobante) {
    try {
      await api.post(`/api/agenda-comprobantes/${fila.id}/reintentar-marcado`, {})
      showToast('Marcado en Agenda', 'success')
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'Agenda sigue sin aceptar el marcado', 'error')
    }
  }

  async function verPdf(fila: AgendaComprobante) {
    try {
      const { url } = await api.get<{ url: string }>(`/api/agenda-comprobantes/${fila.id}/pdf`)
      window.open(url, '_blank', 'noopener')
    } catch (err: any) {
      showToast(err?.message || 'No se pudo abrir el PDF', 'error')
    }
  }

  function abrirDescarte(fila: AgendaComprobante) {
    setDescartar(fila)
    setMotivo('')
    setRefAgenda('')
    setSacarDeAgenda(true)
  }

  async function confirmarDescarte() {
    if (!descartar) return
    setGuardando(true)
    try {
      await api.post(`/api/agenda-comprobantes/${descartar.id}/descartar`, {
        motivo: motivo.trim(),
        ref: sacarDeAgenda ? (refAgenda.trim() || 'NO-EMITIDO') : undefined,
      })
      showToast('Orden descartada', 'success')
      setDescartar(null)
      await cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo descartar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const resumen = useMemo(() => {
    const mes = new Date().toISOString().slice(0, 7)
    const pendientes = filas.filter(f => f.estado === 'pendiente')
    return {
      pendientes: pendientes.length,
      enRiesgo: pendientes.filter(f => (f.dias_habiles ?? 0) > LIMITE_DIAS_HABILES).length,
      porEnviar: filas.filter(f => f.estado === 'emitido').length,
      totalMes: filas
        .filter(f => f.estado !== 'descartado' && String(f.cobrado_en).slice(0, 7) === mes)
        .reduce((s, f) => s + (f.monto || 0), 0),
    }
  }, [filas])

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase()
    return filas.filter(f => {
      if (filtroEstado === 'activos' ? f.estado === 'descartado' : filtroEstado !== 'todos' && f.estado !== filtroEstado) {
        return false
      }
      if (!q) return true
      return [f.nombre, f.doc_numero, f.serie_numero, f.mp_pago_id, f.payer_email, f.concepto]
        .some(v => String(v || '').toLowerCase().includes(q))
    })
  }, [filas, search, filtroEstado])

  const hayFiltros = search.trim() !== '' || filtroEstado !== 'activos'
  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtradas, 15, `${search}|${filtroEstado}`)

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="comprobantes-agenda">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === filas.length
          ? `${filas.length} orden${filas.length !== 1 ? 'es' : ''}`
          : `${total} de ${filas.length}`}
        actions={
          <button className="btn btn-primary" onClick={sincronizar} disabled={sincronizando}
            title="Lee los cobros aprobados de Agenda y los encola. No emite nada.">
            <IconRefresh size={15} /> {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        }
      />

      <KpiStrip>
        <KpiRow moneda="PEN">
          <StatCard label="Pendientes" value={resumen.pendientes} tone={resumen.pendientes > 0 ? 'warning' : 'default'} />
          <StatCard label="Fuera de plazo" value={resumen.enRiesgo} tone={resumen.enRiesgo > 0 ? 'danger' : 'default'} />
          <StatCard label="Por enviar" value={resumen.porEnviar} tone={resumen.porEnviar > 0 ? 'info' : 'default'} />
          <StatCard label="Cobrado este mes" value={formatCurrency(resumen.totalMes, 'PEN')} tone="success" />
        </KpiRow>
      </KpiStrip>

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por nombre, documento, serie o pago de MP..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 180 }}>
          <option value="activos">Sin descartados</option>
          <option value="todos">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear"
            onClick={() => { setSearch(''); setFiltroEstado('activos') }}>Limpiar filtros</button>
        )}
      </div>

      {filas.length === 0 ? (
        <div className="empty-state">
          <h3>Sin comprobantes en cola</h3>
          <p>Cuando alguien pague una membresía de Conecta Agenda, su comprobante aparece acá para emitirlo.</p>
          <button className="btn btn-primary" onClick={sincronizar} disabled={sincronizando}>Sincronizar con Agenda</button>
        </div>
      ) : (
        <>
        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th>Cobrado</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th>Importe</th>
                <th>Plan</th>
                <th>Comprobante</th>
                <th>Estado</th>
                <th style={{ width: 150 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginated.map(f => {
                const vencido = f.estado === 'pendiente' && (f.dias_habiles ?? 0) > LIMITE_DIAS_HABILES
                return (
                  <tr key={f.id} className={f.estado === 'descartado' ? 'row--dimmed' : undefined}>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-date">{formatDate(f.cobrado_en)}</span>
                        <span className={vencido ? 'cell-stack__sub cell-alert' : 'cell-stack__sub'}>
                          {vencido && <IconAlertTriangle size={11} />}{' '}
                          {f.estado === 'pendiente'
                            ? `${f.dias_habiles ?? 0} día${(f.dias_habiles ?? 0) !== 1 ? 's' : ''} hábil${(f.dias_habiles ?? 0) !== 1 ? 'es' : ''}`
                            : f.mp_pago_id ? `MP ${f.mp_pago_id}` : '—'}
                        </span>
                      </div>
                    </td>
                    <td><Badge>{f.comprobante_tipo}</Badge></td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{f.nombre || 'Sin nombre'}</span>
                        <span className="cell-stack__sub">{f.doc_tipo} {f.doc_numero} · {f.payer_email}</span>
                      </div>
                    </td>
                    <td className="cell-amount">
                      <div className="cell-stack">
                        <span>{formatCurrency(f.monto, f.moneda)}</span>
                        <span className="cell-stack__sub">IGV {formatCurrency(f.igv ?? 0, f.moneda)}</span>
                      </div>
                    </td>
                    <td className="cell-soft">{f.plan || '—'} / {f.periodo || '—'}</td>
                    <td>
                      {f.serie_numero
                        ? <button className="btn btn-sm btn-secondary" onClick={() => verPdf(f)} title="Ver el PDF">
                            <IconExternalLink size={13} /> {f.serie_numero}
                          </button>
                        : <span className="cell-soft">—</span>}
                    </td>
                    <td>
                      <div className="cell-stack">
                        <Badge variant={ESTADO_BADGE[f.estado]}>{ESTADO_LABEL[f.estado] || f.estado}</Badge>
                        {f.marcado_error && !f.marcado_at && (
                          <span className="cell-stack__sub cell-alert">Agenda sin marcar</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="row-actions row-actions--nowrap">
                        {f.estado === 'pendiente' && (
                          <>
                            <button className="btn btn-sm btn-primary" onClick={() => abrirEmision(f)} title="Subir el comprobante emitido">
                              <IconUpload size={14} /> Subir
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => abrirDescarte(f)} title="Descartar sin emitir">
                              <IconBan size={14} />
                            </button>
                          </>
                        )}
                        {f.estado === 'emitido' && (
                          <>
                            <button className="btn btn-sm btn-primary" onClick={() => enviar(f)} title={`Enviar a ${f.payer_email}`}>
                              <IconSend size={14} /> Enviar
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => abrirEmision(f)} title="Reemplazar el PDF o la serie">
                              <IconUpload size={14} />
                            </button>
                          </>
                        )}
                        {f.estado === 'enviado' && (
                          f.marcado_at
                            ? <span className="cell-soft"><IconCheckCircle size={14} /></span>
                            : <button className="btn btn-sm btn-secondary" onClick={() => reintentarMarcado(f)} title="Reintentar el marcado en Agenda">
                                <IconRefresh size={14} /> Marcar
                              </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="órdenes" />
        </>
      )}

      {/* ─── Modal: subir el comprobante emitido ─── */}
      <Modal isOpen={emitir !== null} onClose={() => setEmitir(null)}
        title={`Comprobante de ${emitir?.nombre || 'la membresía'}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setEmitir(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={confirmarEmision}
              disabled={guardando || subiendo || !serieNumero.trim() || !(lectura || emitir?.pdf_r2_key)}>
              {guardando ? 'Registrando...' : 'Registrar y asentar en caja'}
            </button>
          </>
        }>
        {emitir && (
          <>
            <div className="form-group">
              <label className="form-label">Datos del comprobante a emitir</label>
              <div className="card" style={{ padding: 12, display: 'grid', gap: 4 }}>
                <div><strong>{emitir.comprobante_tipo === 'factura' ? 'Factura' : 'Boleta'}</strong> a {emitir.doc_tipo} {emitir.doc_numero}</div>
                <div>{emitir.nombre}{emitir.direccion ? ` · ${emitir.direccion}` : ''}</div>
                <div>{emitir.concepto}</div>
                <div>
                  {formatCurrency(emitir.monto, emitir.moneda)} — base {formatCurrency(emitir.base_imponible ?? 0, emitir.moneda)}
                  {' '}+ IGV {formatCurrency(emitir.igv ?? 0, emitir.moneda)}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">PDF del comprobante *</label>
              <input className="form-input" type="file" accept="application/pdf"
                onChange={e => { const f = e.target.files?.[0]; if (f) void subirPdf(f) }} />
              {subiendo && <p className="form-hint">Subiendo y leyendo el documento...</p>}
              {archivo && !subiendo && <p className="form-hint">{archivo.name}</p>}
              {!archivo && emitir.pdf_r2_key && (
                <p className="form-hint">Ya hay un PDF cargado. Subí otro solo si querés reemplazarlo.</p>
              )}
            </div>

            {lectura && lectura.avisos.length > 0 && (
              <div className="form-group">
                <div className="form-alert">
                  <strong>Revisá antes de confirmar</strong>
                  <ul>
                    {lectura.avisos.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Serie y número *</label>
              <input className="form-input" value={serieNumero} placeholder="B001-00000123"
                onChange={e => setSerieNumero(e.target.value.toUpperCase())} />
              <p className="form-hint">
                Es lo que el cliente va a ver en Agenda. Se escribe cuando el correo salga, no antes.
              </p>
            </div>
          </>
        )}
      </Modal>

      {/* ─── Modal: descartar ─── */}
      <Modal isOpen={descartar !== null} onClose={() => setDescartar(null)} title="Descartar la orden"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDescartar(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={confirmarDescarte} disabled={guardando || motivo.trim().length < 3}>
              {guardando ? 'Descartando...' : 'Descartar'}
            </button>
          </>
        }>
        {descartar && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
              No se emite ningún comprobante por el cobro de {formatCurrency(descartar.monto, descartar.moneda)}
              {descartar.nombre ? ` de ${descartar.nombre}` : ''}.
            </p>
            <div className="form-group">
              <label className="form-label">Motivo *</label>
              <input className="form-input" value={motivo} onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: cobro de prueba, los datos no son reales" />
            </div>
            <div className="form-group">
              <label className="form-label">
                <input type="checkbox" checked={sacarDeAgenda} onChange={e => setSacarDeAgenda(e.target.checked)} />
                {' '}Sacarlo también de la cola de Agenda
              </label>
              {sacarDeAgenda && (
                <>
                  <input className="form-input" value={refAgenda} onChange={e => setRefAgenda(e.target.value.toUpperCase())}
                    placeholder="NO-EMITIDO" />
                  <p className="form-hint">
                    Esta referencia queda visible en el panel del cliente. Sin este paso el cobro
                    vuelve a aparecer en cada sincronización.
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
